import { useState } from "react";
import { Box, Link } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "i18n-fe";

// #3682 -- the one way a staff banner lists items that can be as many as the
// show has (classes, stations, sheets). A banner must never grow one line per
// item (Jaybill, 2026-10-23), so this renders at most `max` lines (default 3)
// and then "and N more".
//
//   - `moreTo` given: "and N more" is a link to that (filtered) page.
//   - otherwise: "and N more" is a button that expands the rest in place,
//     inside a box of fixed height that scrolls, so even the expanded banner
//     stays a fixed size.
//
// The first `max` lines are whatever `renderItem` returns, so each keeps its
// own reason and fix link (ADR 0004); this component never rewrites a line.
// `items` is an array of { key, ...anything }; `renderItem(item)` returns the
// line's content. `bulleted` draws list bullets (for the <ul> banners that
// always had them); the station banners' lines are plain.
export const BANNER_LIST_MAX = 3;
const EXPANDED_MAX_HEIGHT_PX = 160;

export default function BannerList({
  items,
  renderItem,
  max = BANNER_LIST_MAX,
  moreTo = null,
  bulleted = false,
  testId = "banner-list",
  sx,
}) {
  const { t } = useTranslation("scoreadmin");
  const [expanded, setExpanded] = useState(false);
  if (!items || items.length === 0) return null;

  const shown = items.slice(0, max);
  const rest = items.slice(max);
  const listSx = {
    listStyle: bulleted ? "disc" : "none",
    m: 0,
    pl: bulleted ? 2.5 : 0,
  };
  const line = (item) => (
    <li key={item.key} data-testid={`${testId}-line`}>
      {renderItem(item)}
    </li>
  );

  return (
    <Box sx={sx} data-testid={testId}>
      <Box component="ul" sx={listSx}>
        {shown.map(line)}
      </Box>
      {rest.length > 0 && (
        <Box sx={{ mt: 0.5 }}>
          {moreTo ? (
            <Link component={RouterLink} to={moreTo} color="inherit" underline="always" data-testid={`${testId}-more`}>
              {t("common.bannerListMore", { count: rest.length })}
            </Link>
          ) : (
            <Link
              component="button"
              type="button"
              color="inherit"
              underline="always"
              aria-expanded={expanded}
              onClick={() => setExpanded((v) => !v)}
              data-testid={`${testId}-more`}
              sx={{ font: "inherit", verticalAlign: "baseline" }}
            >
              {expanded ? t("common.bannerListLess") : t("common.bannerListMore", { count: rest.length })}
            </Link>
          )}
        </Box>
      )}
      {rest.length > 0 && !moreTo && expanded && (
        <Box
          component="ul"
          data-testid={`${testId}-expanded`}
          sx={{ ...listSx, mt: 0.5, maxHeight: EXPANDED_MAX_HEIGHT_PX, overflowY: "auto" }}
        >
          {rest.map(line)}
        </Box>
      )}
    </Box>
  );
}
